import { View, StyleSheet } from "react-native";
import { trpc } from "@/trpc/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Text } from "../ui/text";
import { useCrew } from "@/context/CrewProvider";
import colors from "@/constants/Colors";

export default function MyProbationProgress() {
  const { currentCrewMember } = useCrew();

  const { data: checklistData, isLoading } = trpc.probationChecklist.list.useQuery({
    crew_id: currentCrewMember?.id!,
    // No job_date_id - get overall progress
    show_completed: true, // Show ALL items including completed ones
  }, {
    enabled: !!currentCrewMember?.id,
  });

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center p-4">
        <Text className="text-foreground">Loading your probation checklist...</Text>
      </View>
    );
  }

  if (!checklistData || checklistData.data.length === 0) {
    return (
      <View className="flex-1 items-center justify-center p-4">
        <Text className="text-foreground text-center text-lg font-semibold mb-2">
          🎉 Congratulations!
        </Text>
        <Text className="text-foreground text-center">
          You've completed all probation checklist items!
        </Text>
      </View>
    );
  }

  // Group items by their group
  const groupedItems = checklistData.data.reduce((acc, item) => {
    const groupName = item.probation_checklist_group?.name || 'Other';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(item);
    return acc;
  }, {} as Record<string, typeof checklistData.data>);

  // Calculate overall progress
  const totalItems = checklistData.data.length;
  const totalChecksRequired = checklistData.data.reduce((sum, item) => sum + item.number_of_values, 0);
  const totalChecksCompleted = checklistData.data.reduce((sum, item) => sum + (item.crew_probation_checklists?.length || 0), 0);

  return (
    <View className="flex-1 gap-4">

      {/* Overall Progress Card */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Overall Progress</CardTitle>
          <CardDescription>
            Your probation checklist completion status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <View className="bg-muted rounded-lg p-4">
            <Text className="text-2xl font-bold text-foreground text-center mb-2">
              {totalChecksCompleted} / {totalChecksRequired}
            </Text>
            <Text className="text-sm text-muted-foreground text-center">
              Total checks completed across {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </Text>
          </View>
          <View className="mb-4 p-4 bg-muted rounded-lg">
            <Text className="text-sm text-muted-foreground text-center">
              Your crew chief or senior crew members will check off items as you complete them during jobs.
            </Text>
          </View>
        </CardContent>
      </Card>

      {/* Grouped Checklist Items */}
      {Object.entries(groupedItems).map(([groupName, items]) => (
        <Card key={groupName} className="mb-4">
          <CardHeader>
            <CardTitle>{groupName}</CardTitle>
            <CardDescription>
              Items you need to complete during your probation
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-4">
            {items.map((item) => {
              const completedCount = item.crew_probation_checklists?.length || 0;
              const totalRequired = item.number_of_values;
              const progressPercentage = Math.round((completedCount / totalRequired) * 100);

              // Determine progress bar color based on percentage
              const getProgressColor = (percentage: number) => {
                if (percentage <= 40) return '#EF4444'; // Red
                if (percentage <= 70) return '#F59E0B'; // Amber
                return '#10B981'; // Green
              };

              const progressColor = getProgressColor(progressPercentage);

              return (
                <View
                  key={item.id}
                  className="py-3 border-b border-border"
                >
                  <View className="flex-row items-center justify-between mb-2">
                    <Text className="text-foreground font-medium flex-1 pr-4">
                      {item.name}
                    </Text>
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>
                        {completedCount}/{totalRequired}
                      </Text>
                    </View>
                  </View>

                  {/* Progress Bar */}
                  <View className="h-2 bg-muted rounded-full overflow-hidden">
                    <View
                      className="h-full rounded-full"
                      style={{
                        width: `${progressPercentage}%`,
                        backgroundColor: progressColor
                      }}
                    />
                  </View>

                  <Text className="text-muted-foreground text-xs mt-1">
                    {progressPercentage}% complete
                  </Text>
                </View>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    backgroundColor: colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 999,
  },
  badgeText: {
    color: colors.accent,
    fontSize: 14,
    fontWeight: 'bold',
  },
});

